/**
 * /go/wa landing helpers.
 *
 * The WhatsApp Ref must be the tracking_id stored by POST /api/campaign/track.
 * api.web ignores a client-supplied id and sets tracking_id itself (or returns
 * the existing row for the same gclid). A Ref invented in the browser would
 * not match that row, so sportsman could not link the lead.
 */

import { readStoredAttribution } from './campaignTrack.js';

export const WHATSAPP_PHONE = '917868880000';

export const TRACKING_ID_STORAGE_KEY = 'tracking_id';
export const TRACKING_CLICK_KEY_STORAGE_KEY = 'tracking_click_key';

export const CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];

export const TRACK_PARAM_KEYS = [
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
  'lead_id',
  'lead_type',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'fbclid',
  'msclkid',
  'cid',
  'gid',
  'crid',
  'kw',
  'net',
];

const ATHOME_MESSAGES = {
  bengaluru: 'Hi, I want to enquire about Able Aura At-Home in Bengaluru',
  bangalore: 'Hi, I want to enquire about Able Aura At-Home in Bengaluru',
  blr: 'Hi, I want to enquire about Able Aura At-Home in Bengaluru',
  chennai: 'Hi, I want to enquire about Able Aura At-Home in Chennai',
};

const ACADEMY_MESSAGES = {
  bengaluru: 'Hi, I want to enquire about Able Aura Sports Academy in Bengaluru',
  bangalore: 'Hi, I want to enquire about Able Aura Sports Academy in Bengaluru',
  blr: 'Hi, I want to enquire about Able Aura Sports Academy in Bengaluru',
  chennai: 'Hi, I want to enquire about Able Aura Sports Academy in Chennai',
};

export const CRAWLER_UA =
  /AdsBot-Google|Googlebot|Mediapartners-Google|Google-InspectionTool|Storebot-Google|Bingbot|Slurp|DuckDuckBot|facebookexternalhit|LinkedInBot/i;

export const messageFor = (city, offer) => {
  const cityKey = String(city || '').toLowerCase();
  const offerKey = String(offer || '').toLowerCase();
  const isAcademy = offerKey === 'academy';
  const map = isAcademy ? ACADEMY_MESSAGES : ATHOME_MESSAGES;
  if (map[cityKey]) return map[cityKey];
  return isAcademy
    ? 'Hi, I want to enquire about Able Aura Sports Academy'
    : 'Hi, I want to enquire about Able Aura At-Home';
};

export const appendRefToMessage = (message, trackingId) => {
  const base = message || '';
  if (!trackingId) return base;
  if (base.includes('Ref:') || base.includes('Tracking ID:')) return base;
  return `${base} (Ref: ${trackingId})`;
};

export const buildWhatsAppUrl = (message) =>
  `https://api.whatsapp.com/send?phone=${WHATSAPP_PHONE}&text=${encodeURIComponent(message || '')}`;

/**
 * Same-origin hop used when the WhatsApp Ref is not in the href yet.
 * The static href carries the prefilled text, and /go/wa reads click ids
 * from this query string or from the same-origin referrer.
 */
export const buildGoWaUrl = ({ text, city, offer, from, search } = {}) => {
  const params = new URLSearchParams();
  if (search) {
    const extra = new URLSearchParams(String(search).replace(/^\?/, ''));
    TRACK_PARAM_KEYS.forEach((key) => {
      const value = readParam(extra, key);
      if (value) params.set(key, value);
    });
  }
  if (city) params.set('city', String(city));
  if (offer) params.set('offer', String(offer));
  const message = text ? String(text).slice(0, 300) : '';
  if (message) params.set('text', message);
  const fromPath = from ? String(from) : '';
  if (fromPath.startsWith('/') && !fromPath.startsWith('//') && !fromPath.includes('\\')) {
    params.set('from', fromPath.slice(0, 200));
  }
  const qs = params.toString();
  return qs ? `/go/wa?${qs}` : '/go/wa';
};

/** Prefill for /go/wa. An explicit text param wins over the city/offer map. */
export const resolveGoMessage = (search) => {
  const params = new URLSearchParams(search || '');
  const text = readParam(params, 'text').slice(0, 300);
  if (text) return text;
  return messageFor(readParam(params, 'city'), readParam(params, 'offer'));
};

const readParam = (params, key) => {
  if (!params) return '';
  const value = typeof params.get === 'function' ? params.get(key) : params[key];
  if (value == null) return '';
  return String(value).trim();
};

/** Stable key for the ad click. Empty when this landing has no click id. */
export const buildClickKey = (params) => {
  const values = CLICK_ID_KEYS.map((key) => readParam(params, key));
  if (!values.some(Boolean)) return '';
  return values.join('\n');
};

export const campaignTrackRequestKey = (clickKey, landingPage) =>
  `${clickKey || ''}||${landingPage || ''}`;

/**
 * Site-wide rule: reuse a stored id when this URL is not a new ad click,
 * or when the stored id was saved for this same click id.
 * A page with no click id must keep the Ref from the ad landing.
 */
export const shouldReuseStoredTrackingId = ({ storedId, storedKey, clickKey }) => {
  if (!storedId) return false;
  if (!clickKey) return true;
  return storedKey === clickKey;
};

/**
 * Stricter rule for the ad landing page. A stored id is copied into the
 * WhatsApp text only when it was saved for this gclid/gbraid/wbraid/fbclid/msclkid.
 * The id from this page's own track response is used once that request finishes.
 */
export const selectTrackingId = ({
  expectedClickKey,
  expectedRequestKey,
  memoryReady,
  memoryRequestKey,
  memoryId,
  storedId,
  storedKey,
}) => {
  if (
    memoryReady
    && memoryId
    && expectedRequestKey
    && memoryRequestKey === expectedRequestKey
  ) {
    return String(memoryId).trim();
  }
  if (shouldReuseStoredTrackingId({
    storedId,
    storedKey,
    clickKey: expectedClickKey,
  })) {
    return String(storedId).trim();
  }
  return '';
};

export const readTrackingIdForPage = (expectedClickKey, expectedRequestKey) => {
  if (typeof window === 'undefined') return '';
  let storedId = '';
  let storedKey = '';
  let storage = null;
  try {
    storage = window.localStorage;
  } catch (err) {
    storage = null;
  }
  const record = readStoredAttribution({
    localStorage: storage,
    cookie: typeof document !== 'undefined' ? document.cookie : '',
  });
  if (record) {
    storedId = record.id;
    storedKey = record.clickKey || '';
  }
  return selectTrackingId({
    expectedClickKey: expectedClickKey || '',
    expectedRequestKey: expectedRequestKey || '',
    memoryReady: window.__ableauraWaTrackingReady === true,
    memoryRequestKey: window.__ableauraWaTrackingRequestKey || '',
    memoryId: window.__ableauraWaTrackingId || '',
    storedId: String(storedId).trim(),
    storedKey,
  });
};

const normalizeApiBase = (apiBase) => String(apiBase || '').trim().replace(/\/$/, '');

/**
 * ES5 boot script. It is inlined into the HTML so the button and the track
 * POST do not wait for the Gatsby bundle. It must not navigate by itself:
 * iOS and in-app browsers drop programmatic opens that are not the click.
 */
export const buildBootScript = ({ apiBase } = {}) => {
  const payload = {
    apiBase: normalizeApiBase(apiBase),
    phone: WHATSAPP_PHONE,
    athome: ATHOME_MESSAGES,
    academy: ACADEMY_MESSAGES,
    clickKeys: CLICK_ID_KEYS,
    paramKeys: TRACK_PARAM_KEYS,
    idStorageKey: TRACKING_ID_STORAGE_KEY,
    clickStorageKey: TRACKING_CLICK_KEY_STORAGE_KEY,
  };

  return `(function () {
  try {
    if (window.__ableauraWaBooted) return;
    var CFG = ${JSON.stringify(payload)};
    var anchor = document.getElementById('wa-continue');
    if (!anchor) return;
    window.__ableauraWaBooted = true;

    function val(params, key) {
      var value = params.get(key);
      if (value == null) return '';
      return String(value).replace(/^\\s+|\\s+$/g, '');
    }

    function messageFor(city, offer) {
      var cityKey = String(city || '').toLowerCase();
      var offerKey = String(offer || '').toLowerCase();
      var isAcademy = offerKey === 'academy';
      var map = isAcademy ? CFG.academy : CFG.athome;
      if (map[cityKey]) return map[cityKey];
      return isAcademy
        ? 'Hi, I want to enquire about Able Aura Sports Academy'
        : 'Hi, I want to enquire about Able Aura At-Home';
    }

    function clickKeyFrom(params) {
      var values = [];
      var any = false;
      var i;
      for (i = 0; i < CFG.clickKeys.length; i++) {
        var part = val(params, CFG.clickKeys[i]);
        values.push(part);
        if (part) any = true;
      }
      if (!any) return '';
      return values.join('\\n');
    }

    function safeFrom(value) {
      if (!value || value.charAt(0) !== '/' || value.indexOf('//') === 0 || value.indexOf('\\\\') !== -1) return '';
      return value.slice(0, 200);
    }

    function fillFromReferrer(params) {
      var ref = '';
      try { ref = document.referrer || ''; } catch (err) { return; }
      if (!ref) return;
      var refUrl;
      try { refUrl = new URL(ref); } catch (err) { return; }
      if (refUrl.hostname !== (window.location.hostname || '')) return;
      var i;
      for (i = 0; i < CFG.paramKeys.length; i++) {
        var key = CFG.paramKeys[i];
        if (val(params, key)) continue;
        var copied = val(refUrl.searchParams, key);
        if (copied) params.set(key, copied);
      }
      if (!val(params, 'from')) {
        var path = safeFrom(refUrl.pathname || '');
        if (path && path.indexOf('/go/wa') !== 0) params.set('from', path);
      }
    }

    var params = new URLSearchParams(window.location.search || '');
    fillFromReferrer(params);
    var city = val(params, 'city').toLowerCase();
    var offer = val(params, 'offer').toLowerCase();
    var textParam = val(params, 'text');
    if (textParam.length > 300) textParam = textParam.slice(0, 300);
    var base = textParam || messageFor(city, offer);
    var clickKey = clickKeyFrom(params);
    var requestKey = (clickKey || '') + '||' + (window.location.pathname || '');
    anchor.setAttribute('data-wa-base', base);
    window.__ableauraWaExpectedKey = clickKey;
    window.__ableauraWaExpectedRequestKey = requestKey;

    function cookieValue(name) {
      var all = '';
      try { all = document.cookie || ''; } catch (err) { return ''; }
      var bits = all.split(';');
      var prefix = name + '=';
      var i;
      for (i = 0; i < bits.length; i++) {
        var bit = String(bits[i]).replace(/^\\s+|\\s+$/g, '');
        if (bit.indexOf(prefix) === 0) {
          try { return decodeURIComponent(bit.substring(prefix.length)); }
          catch (err) { return bit.substring(prefix.length); }
        }
      }
      return '';
    }

    function parseRecord(raw) {
      if (!raw) return null;
      try {
        var data = JSON.parse(raw);
        if (!data || !data.id) return null;
        if (data.exp && Number(data.exp) < Date.now()) return null;
        return {
          id: String(data.id).replace(/^\\s+|\\s+$/g, ''),
          key: data.clickKey || ''
        };
      } catch (err) { return null; }
    }

    function stored() {
      var raw = '';
      try { raw = window.localStorage.getItem('tracking_attr') || ''; } catch (err) { raw = ''; }
      var record = parseRecord(raw) || parseRecord(cookieValue('aa_track'));
      if (record && record.id) return record;
      try {
        var expired = raw && JSON.parse(raw).exp && Number(JSON.parse(raw).exp) < Date.now();
        if (expired) return { id: '', key: '' };
      } catch (err) {}
      try {
        var id = window.localStorage.getItem(CFG.idStorageKey) || '';
        var key = window.localStorage.getItem(CFG.clickStorageKey) || '';
        return { id: String(id).replace(/^\\s+|\\s+$/g, ''), key: key };
      } catch (err) {}
      return { id: '', key: '' };
    }

    function gclidFromGclAw() {
      var value = cookieValue('_gcl_aw');
      if (!value) return '';
      var parts = value.split('.');
      if (parts.length >= 3 && parts[0] === 'GCL') return parts.slice(2).join('.');
      return '';
    }

    function persist(id, key, clicks, utm) {
      var record = {
        id: id,
        clickKey: key || '',
        exp: Date.now() + 7776000000,
        gclid: (clicks && clicks.gclid) || '',
        gbraid: (clicks && clicks.gbraid) || '',
        wbraid: (clicks && clicks.wbraid) || '',
        fbclid: (clicks && clicks.fbclid) || '',
        msclkid: (clicks && clicks.msclkid) || '',
        utm_source: (utm && utm.utm_source) || '',
        utm_medium: (utm && utm.utm_medium) || '',
        utm_campaign: (utm && utm.utm_campaign) || '',
        utm_term: (utm && utm.utm_term) || '',
        utm_content: (utm && utm.utm_content) || '',
        utm_id: (utm && utm.utm_id) || ''
      };
      var json = JSON.stringify(record);
      try {
        window.localStorage.setItem(CFG.idStorageKey, id);
        window.localStorage.setItem(CFG.clickStorageKey, key || '');
        window.localStorage.setItem('tracking_attr', json);
      } catch (err) {}
      try {
        document.cookie = 'aa_track=' + encodeURIComponent(json) + '; Path=/; Max-Age=7776000; SameSite=Lax';
      } catch (err) {}
    }

    function readId() {
      if (
        window.__ableauraWaTrackingReady === true &&
        window.__ableauraWaTrackingId &&
        window.__ableauraWaTrackingRequestKey === requestKey
      ) {
        return String(window.__ableauraWaTrackingId).replace(/^\\s+|\\s+$/g, '');
      }
      var saved = stored();
      if (!saved.id) return '';
      if (!clickKey || saved.key === clickKey) return saved.id;
      return '';
    }
    window.__ableauraReadWaTrackingId = readId;

    function applyHref() {
      var id = readId();
      var text = anchor.getAttribute('data-wa-base') || base;
      if (id && text.indexOf('Ref:') === -1 && text.indexOf('Tracking ID:') === -1) {
        text = text + ' (Ref: ' + id + ')';
      }
      anchor.href = 'https://api.whatsapp.com/send?phone=' + CFG.phone + '&text=' + encodeURIComponent(text);
    }

    var savedNow = stored();
    if ((clickKey && savedNow.id && savedNow.key === clickKey) || (!clickKey && savedNow.id)) {
      window.__ableauraWaTrackingReady = true;
      window.__ableauraWaTrackingId = savedNow.id;
      window.__ableauraWaTrackingKey = savedNow.key;
      window.__ableauraWaTrackingRequestKey = requestKey;
    }
    applyHref();

    function onActivate(event) {
      applyHref();
      if (!event || event.type !== 'click') return;
      try {
        window.dataLayer = window.dataLayer || [];
        var id = readId();
        window.dataLayer.push({
          event: 'whatsapp_click',
          source: 'go_wa',
          page: window.location.pathname,
          has_tracking_id: !!id,
          tracking_id: id || null
        });
      } catch (err) {}
    }
    anchor.addEventListener('pointerdown', onActivate, true);
    anchor.addEventListener('click', onActivate, true);
    window.__ableauraWaClickBound = true;

    var ua = (window.navigator && window.navigator.userAgent) || '';
    if (/AdsBot-Google|Googlebot|Mediapartners-Google|Google-InspectionTool|Storebot-Google|Bingbot|Slurp|DuckDuckBot|facebookexternalhit|LinkedInBot/i.test(ua)) {
      return;
    }

    var payload = {};
    var p;
    for (p = 0; p < CFG.paramKeys.length; p++) {
      var paramValue = val(params, CFG.paramKeys[p]);
      if (paramValue) payload[CFG.paramKeys[p]] = paramValue;
    }
    var fromPage = safeFrom(val(params, 'from'));
    payload.landing_page = fromPage || window.location.pathname || '';
    if (document.referrer) payload.referrer = document.referrer;
    payload.device_type = /Mobile|Android|iPhone/i.test(ua) ? 'mobile' : 'desktop';
    // Keep click ids copied from the referrer after this page's own query is empty.
    window.__ableauraAdsSnapshot = payload;
    window.__ableauraAdsSearch = params.toString() ? ('?' + params.toString()) : (window.location.search || '');

    var utmNames = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id'];
    var hasUtm = false;
    var u;
    for (u = 0; u < utmNames.length; u++) if (payload[utmNames[u]]) hasUtm = true;
    var savedForPost = stored();
    if ((clickKey && savedForPost.id && savedForPost.key === clickKey) || (!clickKey && savedForPost.id)) {
      return;
    }
    if (!clickKey && !hasUtm) {
      var gclAw = gclidFromGclAw();
      if (!gclAw) return;
      payload.gclid = gclAw;
      clickKey = gclAw + '\\n\\n\\n\\n';
      requestKey = clickKey + '||' + (window.location.pathname || '');
      window.__ableauraWaExpectedKey = clickKey;
      window.__ableauraWaExpectedRequestKey = requestKey;
      window.__ableauraAdsSnapshot = payload;
    }

    var apiBase = CFG.apiBase;
    if (!apiBase) {
      var host = window.location.hostname || '';
      apiBase = host.indexOf('dev') !== -1
        ? 'https://dev.web.api.ableaura.com/api'
        : 'https://api.web.ableaura.com/api';
    }

    var promise = window.fetch(apiBase + '/campaign/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function (response) {
      if (!response || !response.ok) return null;
      return response.json();
    }).then(function (data) {
      var id = data && data.tracking_id ? String(data.tracking_id).replace(/^\\s+|\\s+$/g, '') : '';
      if (!id) return null;
      persist(id, clickKey, {
        gclid: payload.gclid || '',
        gbraid: payload.gbraid || '',
        wbraid: payload.wbraid || '',
        fbclid: payload.fbclid || '',
        msclkid: payload.msclkid || ''
      }, {
        utm_source: payload.utm_source || '',
        utm_medium: payload.utm_medium || '',
        utm_campaign: payload.utm_campaign || '',
        utm_term: payload.utm_term || '',
        utm_content: payload.utm_content || '',
        utm_id: payload.utm_id || ''
      });
      window.__ableauraWaTrackingReady = true;
      window.__ableauraWaTrackingId = id;
      window.__ableauraWaTrackingKey = clickKey;
      window.__ableauraWaTrackingRequestKey = requestKey;
      applyHref();
      try {
        window.dispatchEvent(new Event('tracking_id_updated'));
      } catch (err) {}
      return id;
    }).catch(function () {
      return null;
    });

    window.__ableauraCampaignTrack = { key: requestKey, promise: promise };
  } catch (err) {}
})();`;
};

/**
 * Id for the click currently on this page. A stored id from a different
 * gclid/gbraid/wbraid/fbclid/msclkid is not reused.
 */
export const readPageTrackingId = () => {
  if (typeof window === 'undefined') return '';
  try {
    if (typeof window.__ableauraReadWaTrackingId === 'function') {
      return window.__ableauraReadWaTrackingId() || '';
    }
  } catch (e) {
    // Fall through to the storage rule.
  }
  const search = window.__ableauraAdsSearch || window.location.search || '';
  const params = new URLSearchParams(search);
  const clickKey = typeof window.__ableauraWaExpectedKey === 'string'
    ? window.__ableauraWaExpectedKey
    : buildClickKey(params);
  const requestKey = typeof window.__ableauraWaExpectedRequestKey === 'string'
    ? window.__ableauraWaExpectedRequestKey
    : campaignTrackRequestKey(clickKey, window.location.pathname || '');
  return readTrackingIdForPage(clickKey, requestKey);
};
